import {
  Button,
  Checkbox,
  Divider,
  Grid,
  LinearProgress,
  Radio,
  Typography,
} from '@material-ui/core';
import TimelineIcon from '@material-ui/icons/Timeline';
import { cloneDeep, debounce } from 'lodash-es';
import moment from 'moment-timezone';
import React from 'react';

import { DashboardEventTypes, track } from '~/src/dash/analytics/analytics';
import {
  DateGroup,
  DateGroupDays,
  DateGroupHours,
  DateGroupMonths,
  DateGroupWeeks,
  getValidDateGroup,
  IAnalytics,
  IDimensions,
  IItemMetrics,
  IRealtimeAnalytics,
  queryAnalytics,
} from '~/src/dash/api/console-api';
import { convertECurrencyTypeToSymbol } from '~/src/dash/api/exchange-rates';
import { getNetworks, INetwork, transformNetworkWrapper } from '~/src/dash/api/network';
import { createUrl, IHttpResponse, restPost } from '~/src/dash/api/rest';
import { Calendar } from '~/src/dash/components/calendar/calendar';
import { IFocusedInput } from '~/src/dash/components/calendar/calendar-wrapper';
import { DashboardCards } from '~/src/dash/components/dashboard/dashboard-cards';
import { IGraphDataTypes } from '~/src/dash/components/dashboard/dashboard-graph';
import { DashboardGraphV2 } from '~/src/dash/components/dashboard/dashboard-graph-v2';
import {
  AerospikeMetricKeys,
  createNewAggregator,
  GraphMetricKeys,
  LiveDataResourceType,
  metricKeyToString,
} from '~/src/dash/components/live-data/live-data';
import { MaterialSearchDropdownMulti } from '~/src/dash/components/material-search-dropdown-multi';
import { MenuButton } from '~/src/dash/components/menu-button';
import { ModelTopBar } from '~/src/dash/components/model-topbar';
import {
  DashboardCardDataTitles,
  DensityRange4HoursByHour,
  DensityRange12HoursByHour,
  DensityRange15Minutes,
  DensityRange24HoursByHour,
  DensityRangeWeekByHour,
  IDensityRange,
  ISelectedMetric,
} from '~/src/dash/globals/constants';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { Panel, Panels } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { ISelectedMetrics } from '~/src/dash/types/metrics';
import {
  IBaseState,
  IHistoryProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { ICalendarQuickSelectorOptions } from '~/src/dash/utils/calendar';
import { publicaColors } from '~/src/dash/utils/colors';
import { generateCountriesMenuList } from '~/src/dash/utils/countries';
import {
  formatDateFromUTCToOffset,
  formatDateStartDay,
  formatWithoutHoursMins,
  getStartDate,
  toEndDate,
  toStartDate,
} from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import {
  allDeviceTypes,
  createAllDeviceTypesMenuList,
  IDeviceType,
} from '~/src/dash/utils/devices';
import { formatHugeNumberToShortenedString } from '~/src/dash/utils/format';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import {
  alignRight,
  backgroundColor,
  container,
  drawerWidth,
  primaryColor,
  whiteColor,
} from '~/src/dash/utils/material-dashboard-react';
import { checkUserInfoMasked, hasRoleSuperUser } from '~/src/dash/utils/roles';
import {
  createAllStreamTypesMenuList,
  IStreamType,
  streamTypes,
} from '~/src/dash/utils/stream-types';
import { getTimezone } from '~/src/dash/utils/timezone';
import {
  FilterActiveChip,
  LiveDataChip,
} from '~/src/dash/views/campaign/campaign-settings-list-view';
import {
  cardDataDisplayError,
  cardDataDisplayKPI,
  cardDataDisplayLatency,
  cardDataDisplayOverview,
  cardDataOverview,
  IDashboardGraphV2State,
  IFilterDimensionState,
  IStreamAggregateCardData,
  IStreamAggregateCardDataDisplay,
  IStreamErrorCardDataDisplay,
  IStreamKPICardDataDisplay,
  IStreamLatencyCardDataDisplay,
  liveDensityRangesNetworkHome,
  liveErrorColorMap,
  liveErrorGraphData,
  liveKPIColorMap,
  liveKPIGraphData,
  liveLatencyColorMap,
  liveLatencyGraphData,
  networkHomeTitlesToKeysMapping,
  selectedMetricsColorMap,
  selectedMetricsError,
  selectedMetricsKPI,
  selectedMetricsLatency,
  selectedMetricsOverview,
  STREAM_ERROR_QUERY_STRING,
  STREAM_KPI_QUERY_STRING,
  STREAM_LATENCY_QUERY_STRING,
  StreamDashboardCardDataKeys,
  StreamDashboardCardDataTitles,
  streamDashboardCardDropdownList,
  streamEventTypeNumberToName,
  ViewMode,
} from '~/src/dash/views/networks/constants';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

/** ******** Parent View Children Interfaces/Objects **********/

interface IProps extends IWithStylesProps, ISessionStateProps, IUserStateProps, IHistoryProps {
  children: React.ReactNode;
}
interface IState extends IBaseState {
  // Timezones
  timezone: string;
  timezonesList: IMenuList;
  // Calendar
  calStartDate: moment.Moment;
  calEndDate: moment.Moment;
  calDisplayStartDate: moment.Moment;
  calDisplayEndDate: moment.Moment;
  compStartDate: moment.Moment;
  compEndDate: moment.Moment;
  compDisplayStartDate: moment.Moment;
  compDisplayEndDate: moment.Moment;
  compareEnabled: boolean;
  calFocusedInput: IFocusedInput;
  compFocusedInput: IFocusedInput;
  densityRangeAgg: DateGroup;
  densityRangeLive: number;
  range1Selected: string;
  range2Selected: string;
  range1DisplaySelected: string;
  range2DisplaySelected: string;
  // Loading Indicators & Debounce
  loading1: LoadingState;
  loading2: LoadingState;
  count1: number;
  // Aggregate Data
  data1: IAnalytics[];
  data2: IAnalytics[];
  // Selected Aggregate Metrics
  selectedMetricsOverview: ISelectedMetric[];
  // Aggregate Cards
  dashboardCardDataDisplay:
    | IStreamAggregateCardDataDisplay
    | IStreamKPICardDataDisplay
    | IStreamLatencyCardDataDisplay
    | IStreamErrorCardDataDisplay;
  dashboardCardDataCompareDisplay:
    | IStreamAggregateCardDataDisplay
    | IStreamKPICardDataDisplay
    | IStreamLatencyCardDataDisplay
    | IStreamErrorCardDataDisplay;
  // Tab selected
  toggleLiveData: ViewMode;
  // Live Data States
  // States for Live Data components
  streamKPIState: IDashboardGraphV2State;
  streamLatencyState: IDashboardGraphV2State;
  streamErrorState: IDashboardGraphV2State;
  // States for filtering by type
  streamTypeState: IFilterDimensionState;
  networkTypeState: IFilterDimensionState;
  deviceTypeState: IFilterDimensionState;
  // Toggles for view all channels and bidders (bidders/demand sources)
  // Indicates that the query might be slow due to many channels, bidders, or duration
  slowLoad: boolean;
  madeFilterChange: boolean;
  // Snackbar & Misc.
  snackbarMessage: string;
  snackbarError: boolean;
  currencySymbol: string;
  [key: string]: any;
}

export const NetworkHomeView = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      section: {
        marginBottom: theme.spacing.unit,
      },
      alignRight,
      paper: {
        paddingLeft: theme.spacing.unit * 2,
        paddingRight: theme.spacing.unit * 2,
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      graphPaper: {
        padding: 8,
        marginBottom: 15,
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      selectedPaper: {
        ...theme.mixins.gutters(),
        boxShadow: '0px 3px 4px #E2E2E2',
      },
      notSelectedPaper: {
        ...theme.mixins.gutters(),
      },
      dashboardCard: {
        backgroundColor: 'white',
        padding: 10,
      },
      dashboardCardTitle: {
        fontWeight: 400,
        fontSize: 12,
        color: '#292F36',
      },
      dashboardCardValue: {
        fontWeight: 600,
        textAlign: 'left',
        fontSize: 20,
        color: '#292F36',
      },
      dashboardCardComparePositive: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#4FC2B5',
      },
      dashboardCardCompareNegative: {
        fontWeight: 400,
        textAlign: 'left',
        fontSize: 12,
        color: '#F05251',
      },
      densityRangeSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#6a686a',
        cursor: 'pointer',
      },
      densityRangeNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      dashboardToggleSelected: {
        fontSize: 12,
        fontWeight: 500,
        cursor: 'pointer',
      },
      dashboardToggleNotSelected: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
        cursor: 'pointer',
      },
      chartName: {
        fontSize: 16,
        fontWeight: 500,
      },
      redArrow: {
        transform: 'rotate(180deg)',
      },
      list: {
        marginTop: '20px',
        paddingLeft: '0',
        paddingTop: '0',
        paddingBottom: '0',
        marginBottom: '0',
        listStyle: 'none',
        position: 'unset',
      },
      appbar: {
        position: 'sticky',
        top: 0,
        marginLeft: 16,
        backgroundColor: primaryColor[0],
      },
      appbarTitle: {
        color: whiteColor,
        flexGrow: 1,
      },
      mainPanel: {
        position: 'relative',
        float: 'right',
        width: `calc(100vw - ${drawerWidth}px)`,
        backgroundColor,
      },
      content: {
        padding: '0',
        minHeight: 'calc(100vh - 64px)',
      },
      container,
      mainTab: {
        color: '#707070',
        textDecoration: 'none',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      mainTabSelected: {
        color: '#5D66FA',
        fontSize: '14px',
        fontWeight: '500',
        paddingTop: '4px',
        pointer: 'cursor',
      },
      unselectedTimeStamp: {
        '&:hover': {
          background: '#eee 0% 0% no-repeat padding-box',
          borderRadius: '5px',
          color: '#555555',
          fontSize: '12px',
        },
        color: '#555555',
        textDecoration: 'none',
        fontSize: '12px',
      },
      selectedTimeStamp: {
        background: '#eee 0% 0% no-repeat padding-box',
        color: '#292F36',
        fontSize: '12px',
        fontWeight: '700',
      },
      dashboardCardValueDisabled: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
      paperCards: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        minHeight: 85,
      },
      paperCardsDisabled: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        boxShadow: '0px 3px 4px #E2E2E2',
        backgroundColor: '#EFEFEF',
        minHeight: 85,
      },
      dashboardCardTitleDisabled: {
        fontSize: 12,
        fontWeight: 500,
        color: '#cecdcf',
      },
      dashboardCardTitleDisabledLarge: {
        fontSize: 20,
        fontWeight: 500,
        color: '#cecdcf',
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    state = {
      timezone: getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
      calStartDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        6,
        'day',
      ).startOf('day'),
      calEndDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        0,
        'day',
      ).endOf('day'),
      calDisplayStartDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        6,
        'day',
      ).startOf('day'),
      calDisplayEndDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        0,
        'day',
      ).endOf('day'),
      compStartDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        13,
        'day',
      ).startOf('day'),
      compEndDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        7,
        'day',
      ).endOf('day'),
      compDisplayStartDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        13,
        'day',
      ).startOf('day'),
      compDisplayEndDate: getStartDate(
        getTimezone(this.props.user.user.Timezone, this.props.session.publisher.Timezone),
        7,
        'day',
      ).endOf('day'),
      compareEnabled: true,
      calFocusedInput: null,
      compFocusedInput: null,
      loading1: LoadingStateLoading,
      loading2: LoadingStateLoading,
      count1: 0,
      data1: [] as IAnalytics[],
      data2: [] as IAnalytics[],
      data1Merged: [] as IAnalytics[],
      dashboardCardDataDisplay: cardDataDisplayOverview,
      dashboardCardDataCompareDisplay: cardDataDisplayOverview,
      dashboardCardData: cardDataOverview,
      range1Selected: 'Last 7 Days',
      range1DisplaySelected: 'Last 7 Days',
      range2Selected: 'Previous Period',
      range2DisplaySelected: 'Compared To',
      timezonesList: {} as IMenuList,
      targetingCountriesList: generateCountriesMenuList(true),
      targetedCountries: [] as string[],
      snackbarMessage: '',
      snackbarError: false,
      slowLoad: false,
      madeFilterChange: false,
      densityRangeAgg: DateGroupDays,
      // TODO: Replace with appropriate density range
      densityRangeLive: DensityRange15Minutes,
      interval: undefined,
      currencySymbol: convertECurrencyTypeToSymbol(this.props.session.publisher.CurrencyDisplayed),
      selectedMetricsOverview,
      toggleLiveData: ViewMode.Overview,
      // States for Live Dashboard
      streamKPIState: {
        loading: LoadingStateLoading,
        selectedMetrics: selectedMetricsKPI,
        data: [] as IRealtimeAnalytics[],
        rawData: [] as IRealtimeAnalytics[],
        cardDataDisplay: cardDataDisplayKPI,
      } as IDashboardGraphV2State,
      streamLatencyState: {
        loading: LoadingStateLoading,
        selectedMetrics: selectedMetricsLatency,
        data: [] as IRealtimeAnalytics[],
        rawData: [] as IRealtimeAnalytics[],
        cardDataDisplay: cardDataDisplayLatency,
      } as IDashboardGraphV2State,
      streamErrorState: {
        loading: LoadingStateLoading,
        selectedMetrics: selectedMetricsError,
        data: [] as IRealtimeAnalytics[],
        rawData: [] as IRealtimeAnalytics[],
        cardDataDisplay: cardDataDisplayError,
      } as IDashboardGraphV2State,
      // States for filtering on dimension
      streamTypeState: {
        selected: [] as string[],
        selectedAll: true,
        forceOpenList: false,
        typeList: createAllStreamTypesMenuList(),
      } as IFilterDimensionState,
      networkTypeState: {
        selected: [] as string[],
        selectedAll: true,
        forceOpenList: false,
        typeList: {} as IMenuList,
      } as IFilterDimensionState,
      deviceTypeState: {
        selected: [] as string[],
        selectedAll: true,
        forceOpenList: false,
        typeList: createAllDeviceTypesMenuList(),
      } as IFilterDimensionState,
    } as IState;

    // loads live data and aggregate data
    _loadDensityData = debounce((count1: number) => {
      if (count1 !== this.state.count1) {
        return;
      }
      this.runAggregateQuery(count1);
    }, 300);

    loadDensityData = (): void => {
      const count1 = this.state.count1 + 1;
      // set loading state loading so the graph won't show the axis until new data has been loaded
      this.setState(
        {
          loading1: LoadingStateLoading,
          loading2: LoadingStateLoading,
          count1,
        },
        () => {
          this._loadDensityData(count1);
        },
      );
    };

    componentDidMount() {
      this.trackEvent('SSAI Dashboard - View');
      this.preloadData();
      const interval = setInterval(() => {
        this.loadRealtimeAnalytics();
      }, 30000);
      this.setState({
        interval,
      });
    }

    componentWillUnmount() {
      // clears the interval for live data
      clearInterval(this.state.interval);
    }

    // get networks via api call, transform them and set state
    getAndLoadNetworks(): void {
      getNetworks(this.props.session.publisher.ID)
        .then((val: IHttpResponse<INetwork[]>) => {
          const networks = [] as INetwork[];

          if (val.parsedBody) {
            for (const item of val.parsedBody) {
              const network = transformNetworkWrapper(item);
              networks.push(network);
            }
          }
          const isUserInfoMasked = checkUserInfoMasked(this.props.user.user);
          const networkList: IMenuList = {};
          networks.forEach((network) => {
            networkList[network.ID] = {
              title: isUserInfoMasked ? 'DEMO_NETWORK_' + network.ID : network.Name,
              selected: false,
              ID: network.ID.toString(),
            };
          });
          this.setState({
            networkTypeState: {
              ...this.state.networkTypeState,
              typeList: networkList,
            },
          });
        })
        .catch(() => {
          this.setState({
            snackbarError: true,
            snackbarMessage: 'Error while getting the list of networks from the API.',
            loading: false,
          });
        });
    }

    // generates network list and timezone then loads tables and graph
    preloadData = () => {
      const preloadStateObj: { [key: string]: IMenuList } = {};

      this.getAndLoadNetworks();
      // generate timezone list
      const timezonesList = genTimezoneList(
        this.state.timezone || this.props.session.publisher.Timezone,
      );
      preloadStateObj.timezonesList = timezonesList;

      this.setState(preloadStateObj, () => {
        // load the graph and tables
        this.loadDensityData();
      });
    };

    trackEvent = (
      eventTitle: DashboardEventTypes,
      graph_id = 0,
      start_date = '',
      end_date = '',
      comp_start_date = '',
      comp_end_date = '',
      timezone = '',
      time_to_load_sec = 0,
      query_failed = false,
      query_duration = 0,
      comp_query_duration_days = 0,
      compare_enabled = false,
    ) => {
      const is_super_user = hasRoleSuperUser(this.props.user.user);
      if (
        eventTitle === 'SSAI Dashboard - Open Calendar' ||
        eventTitle === 'SSAI Dashboard - View' ||
        eventTitle === 'SSAI Dashboard - Open Filter Box' ||
        eventTitle === 'SSAI Dashboard - Open Compare Calendar'
      ) {
        track(
          eventTitle,
          {
            is_super_user,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'SSAI Dashboard - Compare Enabled') {
        track(
          eventTitle,
          {
            is_super_user,
            compare_enabled: this.state.compareEnabled,
          },
          this.props.user.user,
        );
      } else if (eventTitle === 'SSAI Dashboard - Run Agg Query') {
        track(
          eventTitle,
          {
            is_super_user,
            graph_id,
            start_date,
            end_date,
            comp_start_date,
            comp_end_date,
            timezone,
            time_to_load_sec,
            query_failed,
            query_duration_days: query_duration,
            comp_query_duration_days,
            compare_enabled,
          },
          this.props.user.user,
        );
      }
    };

    // will run a parallel request if compareEnabled for the compare calendar
    runAggregateQuery = (count1: number): void => {
      const {
        calStartDate,
        calEndDate,
        compStartDate,
        compEndDate,
        densityRangeAgg,
        compareEnabled,
        timezone,
        networkTypeState,
      } = this.state;
      // query 1 (normal calendar)
      const start_date = formatDateStartDay(calStartDate);
      const end_date =
        this.generateEndDateForSnowflakeQuery(calEndDate).format('YYYY-MM-DDTHH:mm:ss');
      const query_duration_days = calEndDate.diff(calStartDate, 'days') + 1;
      // query 2 (compare calendar)
      const comp_start_date = formatDateStartDay(compStartDate);
      const comp_end_date =
        this.generateEndDateForSnowflakeQuery(compEndDate).format('YYYY-MM-DDTHH:mm:ss');
      const comp_query_duration_days = compEndDate.diff(compStartDate, 'days') + 1;
      // query params
      const graph_id = 73;
      const network_ids = networkTypeState.selectedAll ? '' : [networkTypeState.selected].join(',');
      const stream_types = this.getTypesForQuery('streamTypeState', streamTypes);
      const devicetypes = this.getTypesForQuery('deviceTypeState', allDeviceTypes);
      const grouping = densityRangeAgg;
      /** * Main Graph Queries ***/
      const queries = [
        queryAnalytics({
          query: Object.assign({
            graph_id,
            network_ids,
            start_date,
            end_date,
            timezone,
            grouping,
            stream_types,
            devicetypes,
            publisher_id: this.props.session.publisher.ID,
          }),
        }),
        queryAnalytics({
          query: Object.assign({
            graph_id,
            network_ids,
            start_date: comp_start_date,
            end_date: comp_end_date,
            timezone,
            grouping,
            stream_types,
            devicetypes,
            publisher_id: this.props.session.publisher.ID,
          }),
        }),
      ];

      // Runs the queries together in Promise All
      const queryStartTime = moment().unix();
      Promise.all(queries)
        .then((data1: IAnalytics[][]) => {
          const queryTotalTime = moment().unix() - queryStartTime;
          let queryFailed = false;
          // check the data to make sure it's there, otherwise show an error msg
          if (data1[0].length === 0) {
            queryFailed = true;
            if (
              !compareEnabled &&
              formatDateStartDay(this.state.calEndDate) ===
                formatDateStartDay(this.state.calStartDate)
            ) {
              this.setState({
                loading1: LoadingStateNoData,
                loading2: LoadingStateNoData,
              });
            } else {
              this.setState({
                loading1: LoadingStateNoData,
                loading2: LoadingStateNoData,
              });
            }
            // if there is no compare data (query went wrong or publisher is new), reload the graph without compare
          } else if (compareEnabled && data1[1] && data1[1].length === 0) {
            queryFailed = true;
            this.setState(
              {
                loading1: LoadingStateNoData,
                loading2: LoadingStateNoData,
                snackbarError: false,
                snackbarMessage:
                  'There is no compare data available yet for this period. The dashboard will reload without compare mode enabled.',
                compareEnabled: false,
              },
              () => this.runAggregateQuery(count1),
            );
            // check max date queries, if they didn't return a date, try turning off mergeLiveData and running again
          }
          if (queryFailed) {
            // track events for mixpanel
            this.trackEvent(
              'SSAI Dashboard - Run Agg Query',
              graph_id,
              start_date,
              end_date,
              compareEnabled ? comp_start_date : '',
              compareEnabled ? comp_end_date : '',
              timezone,
              queryTotalTime,
              true,
              query_duration_days,
              compareEnabled ? comp_query_duration_days : 0,
              compareEnabled,
            );
            return;
          }
          if (count1 !== this.state.count1) {
            return;
          }

          // track events for mixpanel
          this.trackEvent(
            'SSAI Dashboard - Run Agg Query',
            graph_id,
            start_date,
            end_date,
            compareEnabled ? comp_start_date : '',
            compareEnabled ? comp_end_date : '',
            timezone,
            queryTotalTime,
            false,
            query_duration_days,
            compareEnabled ? comp_query_duration_days : 0,
            compareEnabled,
          );
          const [dashboardCardData, dashboardCardDataDisplay] = this.createDashboardCardDisplay(
            data1[0],
            false,
          );
          this.setState(
            {
              loading1: LoadingStateHasData,
              loading2: LoadingStateHasData,
              data1: data1[0],
              data2: data1[1],
              dashboardCardDataDisplay,
              dashboardCardData,
            },
            () => {
              if (compareEnabled) {
                const dashboardCardDataCompareDisplay = this.createDashboardCardDisplay(
                  data1[1],
                  true,
                )[1];
                this.setState({
                  dashboardCardDataCompareDisplay,
                });
              }
            },
          );
        })
        .catch(() => {
          // track events for mixpanel
          const queryTotalTime = moment().unix() - queryStartTime;
          this.trackEvent(
            'SSAI Dashboard - Run Agg Query',
            graph_id,
            start_date,
            end_date,
            compareEnabled ? comp_start_date : '',
            compareEnabled ? comp_end_date : '',
            timezone,
            queryTotalTime,
            true,
            query_duration_days,
            compareEnabled ? comp_query_duration_days : 0,
            compareEnabled,
          );
          this.setState({
            loading1: LoadingStateNoData,
            loading2: LoadingStateNoData,
            snackbarMessage: 'Something went wrong: Please try again.',
            snackbarError: true,
          });
        });
    };

    constructToggleData = (
      graphData: IGraphDataTypes,
      selectedMetrics: ISelectedMetric[],
    ): ISelectedMetrics => {
      const toggleData = {} as ISelectedMetrics;
      selectedMetrics.forEach((metric) => {
        const key = networkHomeTitlesToKeysMapping[metric.key];
        if (metric.active) {
          toggleData[key] = graphData[key].color;
        }
      });
      return toggleData;
    };

    updateToggleData = (
      key: StreamDashboardCardDataKeys,
      color: string,
      typeName: string,
    ): ISelectedMetrics => {
      const toggleData = {} as ISelectedMetrics;
      const selectedMetrics = [...this.state[typeName].selectedMetrics] as ISelectedMetric[];
      selectedMetrics.forEach((metric: ISelectedMetric, i) => {
        const currKey = networkHomeTitlesToKeysMapping[metric.key];
        if (key === (currKey as StreamDashboardCardDataKeys)) {
          if (selectedMetrics[i].active) {
            toggleData[key] = color;
          }
          selectedMetrics[i].active = !selectedMetrics[i].active;
        } else {
          selectedMetrics[i].active = false;
        }
      });
      this.setState(
        {
          [typeName]: { ...this.state[typeName], loading: LoadingStateLoading },
        },
        () => {
          this.setState({
            [typeName]: { ...this.state[typeName], selectedMetrics, loading: LoadingStateHasData },
          });
        },
      );
      return toggleData;
    };
    /**
     * Converts metric value to string for latency metrics.
     */
    latencyMetricDisplayLabel = (value: number): string => {
      return value.toFixed(0).toString() + 'ms';
    };
    /**
     * Detects if a metric measures latency by searching for 'Latency' substring.
     */
    detectIfLatencyMetric = (metricKey: StreamDashboardCardDataTitles): boolean => {
      return metricKey.includes('Latency');
    };

    /**
     * Returns different starting Card Data Display templates based on the resourceType.
     * @param {LiveDataResourceType} resourceType Specifies which live data component we are working with.
     * @returns {IStreamDashboardCardDataDisplay} Card Data Display starting template.
     */
    provisionCardDataDisplayTemplate = (resourceType: LiveDataResourceType) => {
      switch (resourceType) {
        case 'StreamKPI':
          return cardDataDisplayKPI;
        case 'StreamLatency':
          return cardDataDisplayLatency;
        case 'StreamError':
          return cardDataDisplayError;
      }
      return cardDataDisplayOverview;
    };
    // TODO: Deprecate the createDashboardCardDisplay with this function
    liveDashboardCardBuilder = (data: IRealtimeAnalytics[], resourceType: LiveDataResourceType) => {
      const liveDashboardCardDataDisplay = this.provisionCardDataDisplayTemplate(resourceType);
      for (const title of Object.values(liveDashboardCardDataDisplay)) {
        let aggregatedValue = 0;
        // Aerospike keys are by design the same as the dashboardKeys we declared
        const aerospikeKey = title.key;
        data.forEach((datum: IRealtimeAnalytics) => {
          if (datum[aerospikeKey]) {
            aggregatedValue += isNaN(datum[aerospikeKey] as number)
              ? 0
              : (datum[aerospikeKey] as number);
          }
          // Insert AS data into CardData Display
          title.value = this.detectIfLatencyMetric(title.key)
            ? this.latencyMetricDisplayLabel(aggregatedValue)
            : formatHugeNumberToShortenedString(aggregatedValue);
        });
      }
      return liveDashboardCardDataDisplay;
    };

    // creates dashboard card display data from query results
    createDashboardCardDisplay = (
      data1Agg: IAnalytics[],
      isCompareCalendar: boolean,
    ): [
      IStreamAggregateCardData,
      (
        | IStreamAggregateCardDataDisplay
        | IStreamKPICardDataDisplay
        | IStreamLatencyCardDataDisplay
        | IStreamErrorCardDataDisplay
      ),
    ] => {
      // aggregate all data from live or agg
      let adFilled = 0,
        podRequests = 0,
        streamError = 0,
        podFilled = 0,
        adImpression = 0,
        totalStreamAdFilledLatency = 0,
        adsFetchTimeout = 0,
        streamChunklistFetch = 0,
        streamChunklistResponse = 0,
        streamStart = 0,
        streamPlaylistTimeout = 0;
      data1Agg.forEach((datum: IAnalytics) => {
        adFilled += isNaN(datum.AdFilled) ? 0 : datum.AdFilled;
        podRequests += isNaN(datum.PodRequest) ? 0 : datum.PodRequest;
        streamError += isNaN(datum.StreamError) ? 0 : datum.StreamError;
        podFilled += isNaN(datum.PodFilled) ? 0 : datum.PodFilled;
        adImpression += isNaN(datum.AdImpression) ? 0 : datum.AdImpression;
        totalStreamAdFilledLatency += isNaN(datum.TotalStreamAdFilledLatency)
          ? 0
          : datum.TotalStreamAdFilledLatency;
        adsFetchTimeout += isNaN(datum.StreamAdsFetchTimeout) ? 0 : datum.StreamAdsFetchTimeout;
        streamChunklistFetch += isNaN(datum.StreamChunklistFetch) ? 0 : datum.StreamChunklistFetch;
        streamChunklistResponse += isNaN(datum.StreamAdChunklistResponse)
          ? 0
          : datum.StreamAdChunklistResponse;
        streamStart += isNaN(datum.StreamStart) ? 0 : datum.StreamStart;
        streamPlaylistTimeout += isNaN(datum.StreamPlaylistTimeout)
          ? 0
          : datum.StreamPlaylistTimeout;
      });
      // calculate metrics off aggregates
      const avgStreamAdFilledLatency = adFilled !== 0 ? totalStreamAdFilledLatency / adFilled : 0;

      let dashboardCardData = {
        'Ads Filled': adFilled,
        'Pod Requests': podRequests,
        'Stream Error': streamError,
        'Pods Filled': podFilled,
        Impressions: adImpression,
        'Fill Latency': avgStreamAdFilledLatency,
        'Fetch Timeout': adsFetchTimeout,
        'Chunklist Fetch': streamChunklistFetch,
        'Chunklist Response': streamChunklistResponse,
        'Stream Start': streamStart,
        'Playlist Timeout': streamPlaylistTimeout,
      } as IStreamAggregateCardData;

      if (isCompareCalendar) {
        dashboardCardData = this.state.dashboardCardData;
      }

      // create strings for display
      const podRequestsString = formatHugeNumberToShortenedString(podRequests);
      const adImpressionString = formatHugeNumberToShortenedString(adImpression);
      const podFilledString = formatHugeNumberToShortenedString(podFilled);
      const adFilledString = formatHugeNumberToShortenedString(adFilled);
      const streamStartString = formatHugeNumberToShortenedString(streamStart);
      const streamChunklistFetchString = formatHugeNumberToShortenedString(streamChunklistFetch);
      const adFillLatencyString = this.latencyMetricDisplayLabel(avgStreamAdFilledLatency);

      // this function runs twice, once for the original data and once for compare
      // the compare data needs the results of the original to calculate the % difference
      const dashboardCardDataDisplay = cloneDeep(cardDataDisplayOverview);
      if (!isCompareCalendar) {
        dashboardCardDataDisplay.Impressions = {
          ...dashboardCardDataDisplay.Impressions,
          value: adImpressionString,
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Fetch Timeout'] = {
          ...dashboardCardDataDisplay['Fetch Timeout'],
          value: adsFetchTimeout.toLocaleString(),
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Ads Filled'] = {
          ...dashboardCardDataDisplay['Ads Filled'],
          value: adFilledString,
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Fill Latency'] = {
          ...dashboardCardDataDisplay['Fill Latency'],
          value: adFillLatencyString,
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Pod Requests'] = {
          ...dashboardCardDataDisplay['Pod Requests'],
          value: podRequestsString,
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Pods Filled'] = {
          ...dashboardCardDataDisplay['Pods Filled'],
          value: podFilledString,
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Stream Error'] = {
          ...dashboardCardDataDisplay['Stream Error'],
          value: streamError.toLocaleString(),
          yAxisID: 'big',
        };
        dashboardCardDataDisplay['Chunklist Fetch'] = {
          ...dashboardCardDataDisplay['Chunklist Fetch'],
          value: streamChunklistFetchString,
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Chunklist Response'] = {
          ...dashboardCardDataDisplay['Chunklist Response'],
          value: streamChunklistResponse.toLocaleString(),
          yAxisID: 'small',
        };
        dashboardCardDataDisplay['Playlist Timeout'] = {
          ...dashboardCardDataDisplay['Playlist Timeout'],
          value: streamPlaylistTimeout.toLocaleString(),
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Stream Start'] = {
          ...dashboardCardDataDisplay['Stream Start'],
          value: streamStartString,
          yAxisID: 'huge',
        };
      } else {
        dashboardCardDataDisplay.Impressions = {
          ...dashboardCardDataDisplay.Impressions,
          value: calculatePercentageChange(dashboardCardData.Impressions || 0, adImpression),
          sign: Math.sign((dashboardCardData.Impressions || 0) - adImpression),
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Fetch Timeout'] = {
          ...dashboardCardDataDisplay['Fetch Timeout'],
          value: calculatePercentageChange(
            dashboardCardData['Fetch Timeout'] || 0,
            adsFetchTimeout,
          ),
          sign: Math.sign((dashboardCardData['Fetch Timeout'] || 0) - adsFetchTimeout),
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Ads Filled'] = {
          ...dashboardCardDataDisplay['Ads Filled'],
          value: calculatePercentageChange(dashboardCardData['Ads Filled'] || 0, adFilled),
          sign: Math.sign((dashboardCardData['Ads Filled'] || 0) - adFilled),
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Fill Latency'] = {
          ...dashboardCardDataDisplay['Fill Latency'],
          value: calculatePercentageChange(
            dashboardCardData['Fill Latency'] || 0,
            avgStreamAdFilledLatency,
          ),
          sign: Math.sign((dashboardCardData['Fill Latency'] || 0) - avgStreamAdFilledLatency),
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Pod Requests'] = {
          ...dashboardCardDataDisplay['Pod Requests'],
          value: calculatePercentageChange(dashboardCardData['Pod Requests'] || 0, podRequests),
          sign: Math.sign((dashboardCardData['Pod Requests'] || 0) - podRequests),
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Pods Filled'] = {
          ...dashboardCardDataDisplay['Pods Filled'],
          value: calculatePercentageChange(dashboardCardData['Pods Filled'] || 0, podFilled),
          sign: Math.sign((dashboardCardData['Pods Filled'] || 0) - podFilled),
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Stream Error'] = {
          ...dashboardCardDataDisplay['Stream Error'],
          value: calculatePercentageChange(dashboardCardData['Stream Error'] || 0, streamError),
          sign: Math.sign((dashboardCardData['Stream Error'] || 0) - streamError),
          yAxisID: 'big',
        };
        dashboardCardDataDisplay['Chunklist Fetch'] = {
          ...dashboardCardDataDisplay['Chunklist Fetch'],
          value: calculatePercentageChange(
            dashboardCardData['Chunklist Fetch'] || 0,
            streamChunklistFetch,
          ),
          sign: Math.sign((dashboardCardData['Chunklist Fetch'] || 0) - streamChunklistFetch),
          yAxisID: 'huge',
        };
        dashboardCardDataDisplay['Chunklist Response'] = {
          ...dashboardCardDataDisplay['Chunklist Response'],
          value: calculatePercentageChange(
            dashboardCardData['Chunklist Response'] || 0,
            streamChunklistResponse,
          ),
          sign: Math.sign((dashboardCardData['Chunklist Response'] || 0) - streamChunklistResponse),
          yAxisID: 'small',
        };
        dashboardCardDataDisplay['Playlist Timeout'] = {
          ...dashboardCardDataDisplay['Playlist Timeout'],
          value: calculatePercentageChange(
            dashboardCardData['Playlist Timeout'] || 0,
            streamPlaylistTimeout,
          ),
          sign: Math.sign((dashboardCardData['Playlist Timeout'] || 0) - streamPlaylistTimeout),
          yAxisID: 'medium',
        };
        dashboardCardDataDisplay['Stream Start'] = {
          ...dashboardCardDataDisplay['Stream Start'],
          value: calculatePercentageChange(dashboardCardData['Stream Start'] || 0, streamStart),
          sign: Math.sign((dashboardCardData['Stream Start'] || 0) - streamStart),
          yAxisID: 'huge',
        };
      }
      return [dashboardCardData, dashboardCardDataDisplay];
    };

    handleSwitchActiveMetric = (idx: number): void => {
      const selectedMetricsOverview = [...this.state.selectedMetricsOverview];
      selectedMetricsOverview[idx].active = !selectedMetricsOverview[idx].active;
      this.setState({
        selectedMetricsOverview,
      });
    };

    handleSwitchCardSelected = (newkey: DashboardCardDataTitles, index: number): void => {
      // swap out metric info for selected metrics
      const selectedMetricsOverview = [...this.state.selectedMetricsOverview];
      selectedMetricsOverview[index] = { key: newkey, active: true };
      this.setState({
        selectedMetricsOverview,
      });
    };

    handleCompareEnabled = () => {
      this.setState(
        (prevState) => {
          return { compareEnabled: !prevState.compareEnabled };
        },
        () => this.trackEvent('SSAI Dashboard - Compare Enabled'),
      );
    };

    // sets calendar start and end dates when focus changes between compare / normal calendar
    handleCalendarFocusedInputChange = (
      isCompareCalendar: boolean,
    ): ((focusedInput: IFocusedInput) => void) => {
      if (isCompareCalendar) {
        return (focusedInput: IFocusedInput) => {
          if (focusedInput === null) {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: null,
              calDisplayStartDate: this.state.calStartDate,
              calDisplayEndDate: this.state.calEndDate,
              compDisplayStartDate: this.state.compStartDate,
              compDisplayEndDate: this.state.compEndDate,
            });
          } else {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: focusedInput,
            });
          }
        };
      } else {
        return (focusedInput: IFocusedInput) => {
          if (focusedInput === null) {
            this.setState({
              calFocusedInput: null,
              compFocusedInput: null,
              calDisplayStartDate: this.state.calStartDate,
              calDisplayEndDate: this.state.calEndDate,
              compDisplayStartDate: this.state.compStartDate,
              compDisplayEndDate: this.state.compEndDate,
            });
          } else {
            this.setState({
              calFocusedInput: focusedInput,
              compFocusedInput: null,
            });
          }
        };
      }
    };

    handleCalendarDateChange = (
      isCompareCalendar: boolean,
    ): ((start: moment.Moment, end: moment.Moment, isCustom: boolean) => void) => {
      if (isCompareCalendar) {
        return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
          if (isCustom) {
            this.handleChangeAggRangeSelected('Custom', true, true);
          }
          this.setState({
            compDisplayEndDate: end,
            compDisplayStartDate: start,
          });
        };
      } else {
        return (start: moment.Moment, end: moment.Moment, isCustom: boolean) => {
          if (isCustom) {
            this.handleChangeAggRangeSelected('Custom', false, true);
          }
          this.setState({
            calDisplayEndDate: end,
            calDisplayStartDate: start,
          });
        };
      }
    };

    handleDateRangeChange = (
      start: moment.Moment,
      end: moment.Moment,
      isCompareCalendar: boolean,
    ): void => {
      const startDate = cloneDeep(start).startOf('day');
      const endDate = cloneDeep(end).endOf('day');

      if (isCompareCalendar) {
        this.setState(
          {
            compStartDate: startDate,
            compEndDate: endDate,
            densityRangeAgg: getValidDateGroup(this.state.densityRangeAgg, start, end),
            page: 0,
          },
          () => {
            this.loadDensityData();
          },
        );
      } else {
        this.setState(
          {
            calStartDate: startDate,
            calEndDate: endDate,
            densityRangeAgg: getValidDateGroup(this.state.densityRangeAgg, start, end),
          },
          () => {
            const compStartDate = this.findCompareStartDate(this.state.timezone);
            const compEndDate = this.findCompareEndDate(this.state.timezone);
            this.setState(
              {
                compDisplayStartDate: compStartDate,
                compDisplayEndDate: compEndDate,
                compStartDate,
                compEndDate,
                page: 0,
              },
              () => {
                this.loadDensityData();
              },
            );
          },
        );
      }
    };

    // changes the range selected (last month, last week, etc) and displayed text
    handleChangeAggRangeSelected = (
      rangeSelected: string,
      isCompareCalendar: boolean,
      applied?: boolean,
    ): void => {
      if (isCompareCalendar) {
        if (applied) {
          this.setState({
            range2Selected: rangeSelected,
            range2DisplaySelected: rangeSelected,
          });
        } else {
          this.setState({ range2DisplaySelected: rangeSelected });
        }
      } else {
        if (applied) {
          this.setState({
            range1Selected: rangeSelected,
            range1DisplaySelected: rangeSelected,
          });
        } else {
          this.setState({ range1DisplaySelected: rangeSelected });
        }
      }
    };

    generateEndDateForSnowflakeQuery = (endDate: moment.Moment): moment.Moment => {
      return endDate;
    };

    handleTimezoneAcceptClick = (src: IMenuList): void => {
      for (const { title, selected } of Object.values(src)) {
        if (selected) {
          // Don't refresh if the timezone hasn't changed
          if (this.state.timezone === title) {
            return;
          }
          const calDaysDiff = this.state.calEndDate.diff(this.state.calStartDate, 'days');
          const compDaysDiff = this.state.compEndDate.diff(this.state.compStartDate, 'days');
          // set dates to new timezone, keep the difference between the days but set to start/end of date
          const calStartDate = toStartDate(this.state.calStartDate.tz(title));
          const calEndDate = toEndDate(moment(calStartDate).add(calDaysDiff, 'days'));
          const compStartDate = toStartDate(this.state.compStartDate.tz(title));
          const compEndDate = toEndDate(moment(compStartDate).add(compDaysDiff, 'days'));
          this.setState({
            madeFilterChange: true,
            calStartDate,
            calEndDate,
            calDisplayStartDate: calStartDate,
            calDisplayEndDate: calEndDate,
            compStartDate,
            compEndDate,
            compDisplayStartDate: compStartDate,
            compDisplayEndDate: compEndDate,
            timezone: title,
          });
          break;
        }
      }
    };

    handleTypeAcceptClick = (typeName: string, values: IMenuList): void => {
      this.errorIfInvalidStateName(typeName);
      const typeList = cloneDeep(this.state[typeName].typeList) as IMenuList;
      const originalValues = Object.values(typeList);
      const isWholeList = Object.values(values).length === originalValues.length;
      const selected = Object.values(values)
        .filter((item: IListItem) => item.selected)
        .map((item: IListItem) => item.ID)
        .sort();

      // Early return if no changes
      if (selected.join(',') === this.state[typeName].selected.join(',')) {
        return;
      }
      // Check if all selected. If all selected, unselect all and set selectedAll field to true
      const allSelected = selected.length === originalValues.length || selected.length === 0;
      if (allSelected) {
        for (const item of Object.values(typeList)) {
          item.selected = false;
        }
        this.setState({
          [typeName]: {
            selected: [],
            selectedAll: true,
            forceOpenList: false,
            typeList,
          },
          madeFilterChange: true,
        });
        return;
      }

      // if all or no networks are selected, switch back to select all networks
      this.setState({
        [typeName]: {
          selected: allSelected ? [] : selected,
          selectedAll: allSelected || selected.length === 0,
          forceOpenList: false,
          typeList: allSelected ? typeList : isWholeList ? values : this.state[typeName].typeList,
        },
        madeFilterChange: true,
      });
    };

    handleViewAll = (typeName: string, selectedAll: boolean) => {
      this.errorIfInvalidStateName(typeName);
      if (selectedAll === this.state[typeName].selectedAll) {
        return;
      }
      this.setState(
        {
          madeFilterChange: selectedAll || (!selectedAll && !!this.state[typeName].selected.length),
          [typeName]: { ...this.state[typeName], selectedAll, forceOpenList: !selectedAll },
        },
        () => {
          if (this.state[typeName].selected.length === 0) {
            return;
          }
        },
      );
    };

    handleDensityRangeChangeAgg = (densityRangeAgg: DateGroup): void => {
      if (densityRangeAgg === DateGroupHours) {
        this.setState(
          {
            densityRangeAgg,
            // switch to today and yesterday for date ranges
            calStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('day'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('day'),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, 1, 'day').startOf('day'),
            compEndDate: getStartDate(this.state.timezone, 1, 'day').endOf('day'),
            compDisplayStartDate: getStartDate(this.state.timezone, 1, 'day').startOf('day'),
            compDisplayEndDate: getStartDate(this.state.timezone, 1, 'day').endOf('day'),
            range1Selected: 'Today',
            range1DisplaySelected: 'Today',
          },
          () => this.loadDensityData(),
        );
      } else if (densityRangeAgg === DateGroupDays) {
        this.setState(
          {
            densityRangeAgg,
            // switch to last 7 days and last last 7 days for date ranges
            calStartDate: getStartDate(this.state.timezone, 6, 'day').startOf('day'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, 6, 'day').startOf('day'),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, 13, 'day').startOf('day'),
            compEndDate: getStartDate(this.state.timezone, 7, 'day').endOf('day'),
            compDisplayStartDate: getStartDate(this.state.timezone, 13, 'day').startOf('day'),
            compDisplayEndDate: getStartDate(this.state.timezone, 7, 'day').endOf('day'),
            range1Selected: 'Last 7 Days',
            range1DisplaySelected: 'Last 7 Days',
          },
          () => this.loadDensityData(),
        );
      } else if (densityRangeAgg === DateGroupWeeks) {
        const daysFromSunday = moment().tz(this.state.timezone).weekday();
        this.setState(
          {
            densityRangeAgg,
            // switch to this week and last week
            calStartDate: getStartDate(this.state.timezone, daysFromSunday, 'day').startOf('day'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, daysFromSunday, 'day').startOf(
              'day',
            ),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, daysFromSunday + 7, 'day').startOf(
              'day',
            ),
            compEndDate: getStartDate(this.state.timezone, daysFromSunday + 1, 'day').endOf('day'),
            compDisplayStartDate: getStartDate(
              this.state.timezone,
              daysFromSunday + 7,
              'day',
            ).startOf('day'),
            compDisplayEndDate: getStartDate(this.state.timezone, daysFromSunday + 1, 'day').endOf(
              'day',
            ),
            range1Selected: 'Last Week (Sun-Sat)',
            range1DisplaySelected: 'Last Week (Sun-Sat)',
          },
          () => this.loadDensityData(),
        );
      } else if (densityRangeAgg === DateGroupMonths) {
        this.setState(
          {
            densityRangeAgg,
            // switch to this month and last month
            calStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('month'),
            calEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            calDisplayStartDate: getStartDate(this.state.timezone, 0, 'day').startOf('month'),
            calDisplayEndDate: getStartDate(this.state.timezone, 0, 'day').endOf('day'),
            compStartDate: getStartDate(this.state.timezone, 0, 'day')
              .startOf('month')
              .add(-1, 'months'),
            compEndDate: getStartDate(this.state.timezone, 0, 'day')
              .endOf('month')
              .add(-1, 'months'),
            compDisplayStartDate: getStartDate(this.state.timezone, 0, 'day')
              .startOf('month')
              .add(-1, 'months'),
            compDisplayEndDate: getStartDate(this.state.timezone, 0, 'day')
              .endOf('month')
              .add(-1, 'months'),
            range1Selected: 'Last Month',
            range1DisplaySelected: 'Last Month',
          },
          () => this.loadDensityData(),
        );
      } else {
        this.setState(
          {
            densityRangeAgg,
          },
          () => this.loadDensityData(),
        );
      }
    };

    findCompareStartDate = (timezone: string): moment.Moment => {
      const calDisplayStartDate = cloneDeep(this.state.calDisplayStartDate);
      const calEndDate = cloneDeep(this.state.calEndDate);
      if (
        this.state.range1Selected === 'This Week(Sun-Today)' ||
        this.state.range1Selected === 'Last Week(Sun-Sat)'
      ) {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'week');
      } else if (
        this.state.range1Selected === 'Last Month' ||
        this.state.range1Selected === 'This Month'
      ) {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'month');
      } else {
        return moment(calDisplayStartDate)
          .tz(timezone)
          .subtract(1 + calEndDate.diff(calDisplayStartDate, 'days'), 'days')
          .startOf('day');
      }
    };

    findCompareEndDate = (timezone: string): moment.Moment => {
      const calDisplayEndDate = cloneDeep(this.state.calDisplayEndDate);
      const calDisplayStartDate = cloneDeep(this.state.calDisplayStartDate);
      if (
        this.state.range1Selected === 'This Week(Sun-Today)' ||
        this.state.range1Selected === 'Last Week(Sun-Sat)'
      ) {
        return moment(calDisplayEndDate).tz(timezone).subtract(1, 'week');
      } else if (
        this.state.range1Selected === 'Last Month' ||
        this.state.range1Selected === 'This Month'
      ) {
        return moment(calDisplayEndDate).tz(timezone).subtract(1, 'month');
      } else {
        return moment(calDisplayStartDate).tz(timezone).subtract(1, 'days').endOf('day');
      }
    };

    clearOneFilterType = (typeName: string): IFilterDimensionState => {
      this.errorIfInvalidStateName(typeName);
      const typeState: IFilterDimensionState = cloneDeep(this.state[typeName]);
      for (const item of Object.values(typeState.typeList)) {
        item.selected = false;
      }
      typeState.selectedAll = true;
      return typeState;
    };

    /**
     * Clears all filters, then loads data
     */
    handleClearFilters = () => {
      const streamTypeState = this.clearOneFilterType('streamTypeState');
      const networkTypeState = this.clearOneFilterType('networkTypeState');
      const deviceTypeState = this.clearOneFilterType('deviceTypeState');
      const timezonesList: IMenuList = cloneDeep(this.state.timezonesList);
      for (const item of Object.values(timezonesList)) {
        if (item.title === this.props.session.publisher.Timezone) {
          item.selected = true;
        } else {
          item.selected = false;
        }
      }
      this.setState(
        {
          streamTypeState,
          networkTypeState,
          deviceTypeState,
          timezone: this.props.session.publisher.Timezone,
          timezonesList,
          madeFilterChange: true,
        },
        () => {
          this.state.toggleLiveData === ViewMode.Overview
            ? this.loadDensityData()
            : this.loadRealtimeAnalytics();
        },
      );
    };

    addRefreshData = (
      oldData: IRealtimeAnalytics[],
      newData: IRealtimeAnalytics[],
    ): IRealtimeAnalytics[] => {
      // if we have a full range of data for the range, remove the oldest item(s)
      const maxLength = this.state.densityRangeLive;
      // shift off last data point if density range 15, 30, 60 or 240
      if (maxLength !== 4 && maxLength !== 12 && maxLength !== 24) {
        while (oldData.length >= maxLength) {
          oldData.shift();
        }
      }
      // since the refresh checks the last 5 minutes, we need to find and replace
      // any minutes from the old data with new data
      for (const newDatum of newData) {
        let matchFound = false;
        for (let i = 0; i < oldData.length; i++) {
          if (oldData[i].Date === newDatum.Date) {
            matchFound = true;
            oldData[i] = newDatum;
            break;
          }
        }
        // if no match is found, the new data will be placed at the end
        if (!matchFound) {
          oldData.push(newDatum);
        }
      }
      // if 4 or 12 or 24 hours by hour, replace the last hour first, then push out an old hour if limit reached
      if (maxLength === 4 || maxLength === 12 || maxLength === 24) {
        while (oldData.length > maxLength) {
          oldData.shift();
        }
      }
      return oldData;
    };
    combineData = (rawData: IRealtimeAnalytics[]): IRealtimeAnalytics[] => {
      const combinedData = [] as IRealtimeAnalytics[];
      // combine raw data into 4 minute periods
      const periodDuration = 4;
      const numPeriods = Math.ceil(rawData.length / periodDuration);
      for (let period = 0; period < numPeriods; period++) {
        // create new aggregator
        const combinedDatum = createNewAggregator(0, '');
        // loop over each minute for a period
        for (let i = 0; i < periodDuration; i++) {
          const minute = period * periodDuration + i;
          // skip if end is reached
          if (minute >= rawData.length) {
            continue;
          }
          const rawDatum = rawData[minute] as IRealtimeAnalytics;
          // if first index, take that date as date for the period
          if (i === 0) {
            combinedDatum.Date = rawDatum.Date;
          }
          // loop over metrics
          for (const metric of Object.keys(rawDatum)) {
            const key = metric as GraphMetricKeys;
            if (metric === 'Date') {
              continue;
            }
            if (!combinedDatum[key]) {
              combinedDatum[key] = 0;
            }
            combinedDatum[key] += (rawDatum[key] as number) || 0;
          }
        }
        combinedData.push(combinedDatum);
      }
      return combinedData;
    };

    aggregateRealtimeAnalytics = (
      resourceType: LiveDataResourceType,
      data: IDimensions,
      endUnixTimestamp: number,
      startUnixTimestamp: number,
      itemskey: string,
      refresh: boolean,
    ) => {
      const { densityRangeLive, timezone } = this.state;
      const minuteDifference = moment
        .unix(endUnixTimestamp)
        .diff(moment.unix(startUnixTimestamp), 'minutes');
      const currentMetricKeys = metricKeyToString(resourceType);
      const selectedMetricsKeys = Object.keys(currentMetricKeys);
      // calculate the number of periods based on minute difference and period duration
      // each period is 1 minute long except the the hour by hour ranges
      let periodDuration = 1;
      let lastPeriodDuration = 1;
      if (
        densityRangeLive === DensityRange4HoursByHour ||
        densityRangeLive === DensityRange12HoursByHour ||
        densityRangeLive === DensityRange24HoursByHour ||
        densityRangeLive === DensityRangeWeekByHour
      ) {
        periodDuration = 60;
        lastPeriodDuration = (endUnixTimestamp - startUnixTimestamp) / 60;
      }
      let numPeriods = Math.ceil(minuteDifference / periodDuration);
      if (
        densityRangeLive === DensityRange4HoursByHour ||
        densityRangeLive === DensityRange12HoursByHour ||
        densityRangeLive === DensityRange24HoursByHour ||
        densityRangeLive === DensityRangeWeekByHour
      ) {
        // if 4/12 hours by hour, 1 period per hour, refresh latest hour
        numPeriods = refresh ? 1 : densityRangeLive;
      }

      const realtimeAnalytics: IRealtimeAnalytics[] = [];
      // for each period determine what metrics need to be summed
      const idsMap = {} as { [key: string]: boolean };
      for (let period = 0; period < numPeriods; period++) {
        // create new aggregator and increment time stamp
        const realtimeAnalytic = createNewAggregator(startUnixTimestamp, timezone);
        startUnixTimestamp += periodDuration * 60;
        // if it is the last period, use the last period duration
        for (
          let minute = 0;
          minute < (period === numPeriods - 1 ? lastPeriodDuration : periodDuration);
          minute++
        ) {
          const currentIndex = minute + periodDuration * period;
          if (itemskey in data) {
            for (const [id, item] of Object.entries(data[itemskey].items)) {
              idsMap[id] = true;
              const metrics = item.metrics as IItemMetrics;
              // iterate over selected metrics to key into the response
              for (let i = 0; i < selectedMetricsKeys.length; i++) {
                const selectedMetric = selectedMetricsKeys[i] as AerospikeMetricKeys;
                if (selectedMetric in metrics) {
                  const selectedMetricString = currentMetricKeys[selectedMetric];
                  const metricString = selectedMetricString as GraphMetricKeys;
                  // data can possibly be null for sites that have no live data
                  if (metrics[selectedMetric].data) {
                    const dataArray = metrics[selectedMetric].data;
                    // check to make sure data array can be indexed and its not null
                    if (
                      dataArray &&
                      dataArray.length > currentIndex &&
                      selectedMetric in currentMetricKeys
                    ) {
                      // if this metric will have a stackedBar graph, need to add it + the id of the corresponding item to realtimeAnalytic
                      // so that the datapoints can later be grouped into one bar
                      if (!realtimeAnalytic[metricString]) {
                        realtimeAnalytic[metricString] = 0;
                      }
                      realtimeAnalytic[metricString] += dataArray[currentIndex];
                    }
                  }
                }
              }
            }
          }
        }
        if (resourceType === 'StreamLatency') {
          realtimeAnalytic.StreamAdFilledLatency =
            realtimeAnalytic.StreamAdFilledLatency && realtimeAnalytic.StreamAdFilled
              ? realtimeAnalytic.StreamAdFilledLatency / realtimeAnalytic.StreamAdFilled
              : 0;
        }
        realtimeAnalytics.push(realtimeAnalytic);
      }
      return realtimeAnalytics;
    };

    async getNetworksRealtimeAnalytics(
      beginTime: string,
      endTime: string,
      metrics: string,
      publisherID: string,
      streamType: string,
      deviceTypes: string,
      streamTypeDeviceType: string,
      networkname: string,
      networknameStreamType: string,
      networknameDeviceType: string,
      networknameStreamTypeDeviceType: string,
    ): Promise<[any?, Error?]> {
      const requestURL = createUrl('/realtime_analytics', publisherID);
      try {
        const response = await restPost<any>(requestURL, {
          begin_time: beginTime,
          end_time: endTime,
          metrics,
          publisher_id: publisherID,
          stream_type: streamType,
          device_types: deviceTypes,
          stream_type_device_type: streamTypeDeviceType,
          networkname,
          networkname_stream_type: networknameStreamType,
          networkname_device_type: networknameDeviceType,
          networkname_stream_type_device_type: networknameStreamTypeDeviceType,
        });
        return [response.parsedBody, undefined];
      } catch (err) {
        console.error(err);
        return [undefined, err];
      }
    }

    removeEmptyGroups = (groups: string[][]) => {
      const nonEmptyGroups = [] as string[][];
      groups.forEach((group: string[]) => {
        if (group.length > 0) {
          nonEmptyGroups.push(group);
        }
      });
      return nonEmptyGroups;
    };

    generateAllCombinations = (groups: string[][], publisherID: string) => {
      // It's the same as:
      // https://leetcode.com/problems/letter-combinations-of-a-phone-number/description/
      // So I do BFS solution
      const nonEmptyGroups = this.removeEmptyGroups(groups);
      if (nonEmptyGroups.length === 0) {
        return '';
      }
      const combos = nonEmptyGroups[0].map((name) => [name]) as string[][];
      let currGroupIndex = 1;
      let combosLeft = combos.length;
      while (currGroupIndex < nonEmptyGroups.length) {
        if (combosLeft > 0) {
          const combo = combos.shift();
          nonEmptyGroups[currGroupIndex].forEach((dim: string) => {
            if (combo !== undefined) {
              combos.push([...combo, dim]);
            }
          });
          combosLeft -= 1;
        } else {
          currGroupIndex += 1;
          combosLeft = combos.length;
        }
      }

      return combos
        .map((group) => {
          return publisherID + '_' + group.join('_');
        })
        .join(',');
    };

    loadRealtimeAnalytics() {
      // Queries realtime consumer for data
      this.loadDensityDataLive(STREAM_KPI_QUERY_STRING, 'streamKPIState', 'StreamKPI', false);
      this.loadDensityDataLive(
        STREAM_LATENCY_QUERY_STRING,
        'streamLatencyState',
        'StreamLatency',
        false,
      );
      this.loadDensityDataLive(STREAM_ERROR_QUERY_STRING, 'streamErrorState', 'StreamError', false);
    }

    loadDensityDataLive = (
      metrics: string,
      childState: string,
      resourceType: LiveDataResourceType,
      refresh?: boolean,
    ): void => {
      const { session } = this.props;
      const { densityRangeLive, networkTypeState, streamTypeState, deviceTypeState } = this.state;
      const startMinAgo = refresh ? -5 : -1 * densityRangeLive + 1;
      let startUnixTimestamp = moment().add(startMinAgo, 'minute').unix();
      if (
        densityRangeLive === DensityRange4HoursByHour ||
        densityRangeLive === DensityRange12HoursByHour ||
        densityRangeLive === DensityRange24HoursByHour ||
        densityRangeLive === DensityRangeWeekByHour
      ) {
        if (refresh) {
          startUnixTimestamp = moment().minute(0).unix();
        } else {
          startUnixTimestamp = moment()
            .minute(0)
            .add(-densityRangeLive + 1, 'hours')
            .unix();
        }
      }
      const beginTimeStr = startUnixTimestamp.toString();
      const endUnixTimestamp = moment().add(1, 'minutes').unix();
      const publisherID = session.publisher.ID;
      const networkTypesSelected = !networkTypeState.selectedAll;
      const streamTypesSelected = !streamTypeState.selectedAll;
      const deviceTypesSelected = !deviceTypeState.selectedAll;

      // In Aerospike, stream types are referenced by their name, e.g. HLS_LIVE
      const streamTypeNames = [] as string[];
      streamTypeState.selected.forEach((type: string) => {
        if (streamEventTypeNumberToName.hasOwnProperty(type)) {
          streamEventTypeNumberToName[type].forEach((streamType: string) => {
            streamTypeNames.push(streamType);
          });
        }
      });

      const realtimePromise: Promise<[any?, (Error | undefined)?]> = new Promise(
        (resolve, reject) => {
          this.getNetworksRealtimeAnalytics(
            beginTimeStr,
            endUnixTimestamp.toString(),
            metrics,
            publisherID,
            streamTypesSelected && !(networkTypesSelected || deviceTypesSelected)
              ? this.generateAllCombinations([streamTypeNames], session.publisher.ID)
              : '',
            deviceTypesSelected && !(networkTypesSelected || streamTypesSelected)
              ? this.generateAllCombinations([deviceTypeState.selected], session.publisher.ID)
              : '',
            streamTypesSelected && deviceTypesSelected && !networkTypesSelected
              ? this.generateAllCombinations(
                  [streamTypeNames, deviceTypeState.selected],
                  session.publisher.ID,
                )
              : '',
            networkTypesSelected && !(deviceTypesSelected || streamTypesSelected)
              ? this.generateAllCombinations([networkTypeState.selected], session.publisher.ID)
              : '',
            networkTypesSelected && streamTypesSelected && !deviceTypesSelected
              ? this.generateAllCombinations(
                  [networkTypeState.selected, streamTypeNames],
                  session.publisher.ID,
                )
              : '',
            networkTypesSelected && deviceTypesSelected && !streamTypesSelected
              ? this.generateAllCombinations(
                  [networkTypeState.selected, deviceTypeState.selected],
                  session.publisher.ID,
                )
              : '',
            networkTypesSelected && streamTypesSelected && deviceTypesSelected
              ? this.generateAllCombinations(
                  [networkTypeState.selected, streamTypeNames, deviceTypeState.selected],
                  session.publisher.ID,
                )
              : '',
          )
            .then((realtimeResponseOrig) => resolve(realtimeResponseOrig))
            .catch((err) => reject(err));
        },
      );

      realtimePromise.then(
        (response) => {
          const [dimensions, error] = response;
          if (error || dimensions.dimensions === undefined) {
            this.setState({
              [childState]: { ...this.state[childState], loading: LoadingStateNoData },
            });
            return;
          }

          let itemKey;
          if (streamTypesSelected && !(networkTypesSelected || deviceTypesSelected)) {
            itemKey = 'stream_type';
          } else if (deviceTypesSelected && !(networkTypesSelected || streamTypesSelected)) {
            itemKey = 'device_type';
          } else if (streamTypesSelected && deviceTypesSelected && !networkTypesSelected) {
            itemKey = 'stream_type_device_type';
          } else if (networkTypesSelected && !(deviceTypesSelected || streamTypesSelected)) {
            itemKey = 'networkname';
          } else if (networkTypesSelected && streamTypesSelected && !deviceTypesSelected) {
            itemKey = 'networkname_stream_type';
          } else if (networkTypesSelected && deviceTypesSelected && !streamTypesSelected) {
            itemKey = 'networkname_device_type';
          } else if (networkTypesSelected && streamTypesSelected && deviceTypesSelected) {
            itemKey = 'networkname_stream_type_device_type';
          } else {
            itemKey = 'publisher_id';
          }

          const realtimeAnalytics = this.aggregateRealtimeAnalytics(
            resourceType,
            dimensions.dimensions,
            endUnixTimestamp,
            startUnixTimestamp,
            itemKey,
            !!refresh,
          );
          let data = [...realtimeAnalytics];
          const dataRaw = [] as IRealtimeAnalytics[];
          if (refresh) {
            data = this.addRefreshData(this.state[childState].rawData, realtimeAnalytics);
          }
          const dashboardCardDataDisplay = this.liveDashboardCardBuilder(data, resourceType);
          this.setState({
            [childState]: {
              ...this.state[childState],
              loading: LoadingStateHasData,
              data,
              rawData: dataRaw,
              cardDataDisplay: dashboardCardDataDisplay,
            },
          });
        },
        () => {
          this.setState({
            [childState]: { ...this.state[childState], loading: LoadingStateNoData },
          });
        },
      );
    };

    handleChangeTab = (tabSelected: ViewMode) => {
      this.setState({
        loading1: LoadingStateLoading,
        toggleLiveData: tabSelected,
      });
      if (tabSelected === ViewMode.LiveData) {
        this.loadRealtimeAnalytics();
        return;
      }
      this.loadDensityData();
    };

    handleDensityRangeChangeLive = (densityRange: number): void => {
      if (this.state.densityRangeLive === densityRange) {
        return;
      }
      this.setState(
        {
          densityRangeLive: densityRange,
        },
        () => {
          this.loadRealtimeAnalytics();
        },
      );
    };

    handleChangeHomeMode = (tabSelected: ViewMode) => {
      this.handleChangeTab(tabSelected);
    };

    snackbarClose = () => {
      this.setState({
        snackbarMessage: '',
        snackbarError: false,
      });
    };

    renderTop() {
      const { classes, session } = this.props;
      return (
        <Panel
          largeHorizontalPadding
          verticalPadding
          style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}
        >
          <ModelTopBar
            title={'SSAI Dashboard'}
            session={this.props.session}
            user={this.props.user}
            publisher={session.publisher}
            classes={classes}
            delegateElevate={true}
            handleChangeHomeMode={() => ''}
            homeMode={false}
            currentPath={this.props.location.pathname}
            disableMultiPub={true}
          />
          <Grid item container direction='column' alignItems='center'>
            <Grid
              container
              justify='flex-start'
              direction='row'
              alignItems='center'
              spacing={1}
              style={{ minHeight: 50 }}
            >
              <Grid item style={{ marginLeft: 16 }}>
                <Grid
                  container
                  justify='space-between'
                  direction='row'
                  alignItems='center'
                  style={{ flexWrap: 'wrap', width: 250 }}
                >
                  <Grid item style={{ cursor: 'pointer' }}>
                    <Grid
                      container
                      className={
                        this.state.toggleLiveData === ViewMode.Overview
                          ? classes.mainTabSelected
                          : classes.mainTab
                      }
                      onClick={() => this.handleChangeHomeMode(ViewMode.Overview)}
                      justify='center'
                      direction='row'
                      alignItems='center'
                      spacing={0}
                    >
                      <Grid item>
                        {this.state.toggleLiveData === ViewMode.Overview ? (
                          <img
                            src='/assets/shared/svg/OverviewFocused.svg'
                            style={{ width: '15px' }}
                          />
                        ) : (
                          <img
                            src='/assets/shared/svg/OverviewUnfocused.svg'
                            style={{ width: '15px' }}
                          />
                        )}
                      </Grid>
                      <Grid item>
                        <strong style={{ marginLeft: 12 }}>OVERVIEW</strong>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item style={{ cursor: 'pointer' }}>
                    <Grid
                      container
                      onClick={() => this.handleChangeHomeMode(ViewMode.LiveData)}
                      className={
                        this.state.toggleLiveData === ViewMode.LiveData
                          ? classes.mainTabSelected
                          : classes.mainTab
                      }
                      justify='center'
                      direction='row'
                      alignItems='center'
                      spacing={0}
                    >
                      <Grid item>
                        <TimelineIcon />
                      </Grid>
                      <Grid item>
                        <strong style={{ marginLeft: 12 }}>LIVE DATA</strong>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <Divider
              variant='fullWidth'
              style={{ height: '1px', marginTop: '3px', marginBottom: '3px' }}
              orientation='horizontal'
            />
            {this.renderFilters()}
            <Divider
              variant='fullWidth'
              style={{ height: '1px', marginTop: '3px', marginBottom: '3px' }}
              orientation='horizontal'
            />
          </Grid>
        </Panel>
      );
    }

    renderFilters() {
      const {
        calEndDate,
        calStartDate,
        compStartDate,
        compEndDate,
        calDisplayEndDate,
        calDisplayStartDate,
        compDisplayStartDate,
        compDisplayEndDate,
        calFocusedInput,
        compFocusedInput,
        compareEnabled,
        range1Selected,
        range2Selected,
        range1DisplaySelected,
        range2DisplaySelected,
        timezone,
      } = this.state;
      const { classes } = this.props;
      // moment uses machine's timezone which needs to be adjusted to timezone in state
      const calendarQuickSelectorOptions: ICalendarQuickSelectorOptions[] = [
        { title: 'Today', date1: () => moment().tz(timezone), date2: () => moment().tz(timezone) },
        {
          title: 'Yesterday',
          date1: () => moment().tz(timezone).subtract(1, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
        {
          title: 'This Week(Sun-Today)',
          date1: () => moment().tz(timezone).subtract(moment().tz(timezone).format('d'), 'days'),
          date2: () => moment().tz(timezone),
        },
        {
          title: 'Last 7 Days',
          date1: () => moment().tz(timezone).subtract(6, 'days'),
          date2: () => moment().tz(timezone).subtract(0, 'days'),
        },
        {
          title: 'Last Week (Sun-Sat)',
          date1: () =>
            moment()
              .tz(timezone)
              .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 7, 'days'),
          date2: () =>
            moment()
              .tz(timezone)
              .subtract(parseInt(moment().tz(timezone).format('d'), 10) + 1, 'days'),
        },
        {
          title: 'This Month',
          date1: () => moment().tz(timezone).startOf('month'),
          date2: () => moment().tz(timezone),
        },
        {
          title: 'Last 30 Days',
          date1: () => moment().tz(timezone).subtract(29, 'days'),
          date2: () => moment().tz(timezone).subtract(0, 'days'),
        },
        {
          title: 'Last Month',
          date1: () => moment().tz(timezone).subtract(1, 'months').date(1),
          date2: () =>
            moment()
              .tz(timezone)
              .subtract(1, 'months')
              .date(moment().tz(timezone).subtract(1, 'months').daysInMonth()),
        },
      ];
      const calendarQuickSelectorCustomOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: ' Days Up To Today',
          date1: (e: number | string) => moment().tz(timezone).subtract(e, 'days'),
          date2: () => moment().tz(timezone),
        },
        {
          title: ' Days Up To Yesterday',
          date1: (e: number | string) => moment().tz(timezone).subtract(e, 'days'),
          date2: () => moment().tz(timezone).subtract(1, 'days'),
        },
      ];
      const compareQuickSelectorOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: 'Previous Period',
          date1: () => this.findCompareStartDate(timezone),
          date2: () => this.findCompareEndDate(timezone),
        },
        {
          title: 'Last Month',
          date1: () => moment(calDisplayStartDate).tz(timezone).subtract(1, 'months'),
          date2: () => moment(calDisplayEndDate).tz(timezone).subtract(1, 'months'),
        },
      ];
      const compareQuickSelectorCustomOptions: ICalendarQuickSelectorOptions[] = [
        {
          title: ' Days Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'days'),
        },
        {
          title: ' Weeks Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'weeks'),
        },
        {
          title: ' Months Ago, Same Period',
          date1: (value: string) =>
            moment(calDisplayStartDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
          date2: (value: string) =>
            moment(calDisplayEndDate).tz(timezone).subtract(parseInt(value, 10), 'months'),
        },
      ];
      const isCalDayHighlighted = (day: moment.Moment): boolean => {
        if (!compareEnabled) {
          return false;
        }
        return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
          compDisplayStartDate,
          compDisplayEndDate,
          'day',
          '[]',
        );
      };
      const isCompDayHighlighted = (day: moment.Moment): boolean => {
        return formatDateFromUTCToOffset(day.format('YYYY-MM-DDTHH:mm:ss'), 'UTC').isBetween(
          calDisplayStartDate,
          calDisplayEndDate,
          'day',
          '[]',
        );
      };
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid
          item
          container
          direction='row'
          justify='space-between'
          alignItems='center'
          spacing={2}
          style={{ margin: -8, minHeight: 65 }}
        >
          {/* left - filter button */}
          <Grid item style={{ marginLeft: 12 }}>
            <Grid container alignItems='center'>
              <Grid item>{this.renderFilterButton()}</Grid>
              {activeFilterCount !== 0 && (
                <Grid item onClick={() => this.handleClearFilters()}>
                  <Typography
                    style={{
                      marginLeft: 10,
                      fontSize: '0.875rem',
                      fontWeight: 400,
                      color: '#5D66FA',
                      textTransform: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
          {this.state.toggleLiveData === ViewMode.Overview ? (
            <Grid item>
              <Grid
                container
                direction='row'
                justify='flex-end'
                alignItems='center'
                spacing={2}
                style={{ width: 'auto' }}
              >
                <Grid item>
                  <Grid
                    container
                    direction='row'
                    justify='flex-end'
                    alignItems='center'
                    spacing={1}
                    style={{ width: 'auto' }}
                  >
                    <Grid item justify='flex-start'>
                      <Typography style={{ fontSize: 12, fontWeight: 500 }}>Period</Typography>
                    </Grid>
                    <Grid item justify='flex-start'>
                      <Typography style={{ fontSize: 12 }}>{range1DisplaySelected}</Typography>
                    </Grid>
                    <Grid item>
                      <Calendar
                        startDate={calStartDate}
                        endDate={calEndDate}
                        calStartDate={calDisplayStartDate}
                        calEndDate={calDisplayEndDate}
                        startDateID='startDate'
                        endDateID='endDate'
                        rangeSelected={range1Selected}
                        handleRangeSelected={this.handleChangeAggRangeSelected}
                        handleCalendarDateChange={this.handleCalendarDateChange(false)}
                        handleDateRangeChange={this.handleDateRangeChange}
                        quickSelectorOptions={calendarQuickSelectorOptions}
                        quickSelectorCustomOptions={calendarQuickSelectorCustomOptions}
                        isDayHighlighted={isCalDayHighlighted}
                        isCompareCalendar={false}
                        focusedInput={calFocusedInput}
                        dateFormat={formatWithoutHoursMins(
                          this.props.session.publisher.Localization,
                        )}
                        handleFocusedInputChange={this.handleCalendarFocusedInputChange(false)}
                        trackOnClose={() => this.trackEvent('SSAI Dashboard - Open Calendar')}
                        timezone={timezone}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid
                    container
                    direction='row'
                    justify='flex-end'
                    alignItems='center'
                    spacing={1}
                    style={{ width: 'auto' }}
                  >
                    <Grid item>
                      <Grid container justify='flex-start' alignItems='center' direction='row'>
                        <Grid item>
                          <Checkbox
                            size='small'
                            checked={compareEnabled}
                            onChange={this.handleCompareEnabled}
                          />
                        </Grid>
                        <Grid item>
                          <Typography style={{ fontSize: 12 }}>{range2DisplaySelected}</Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item>
                      <Grid container direction='column'>
                        <Calendar
                          startDate={compStartDate}
                          endDate={compEndDate}
                          calStartDate={compDisplayStartDate}
                          calEndDate={compDisplayEndDate}
                          startDateID='startDate_Compare'
                          endDateID='endDate_Compare'
                          rangeSelected={range2Selected}
                          handleRangeSelected={this.handleChangeAggRangeSelected}
                          handleCalendarDateChange={this.handleCalendarDateChange(true)}
                          handleDateRangeChange={this.handleDateRangeChange}
                          quickSelectorOptions={compareQuickSelectorOptions}
                          quickSelectorCustomOptions={compareQuickSelectorCustomOptions}
                          isDayHighlighted={isCompDayHighlighted}
                          isCompareCalendar={true}
                          focusedInput={compFocusedInput}
                          dateFormat={formatWithoutHoursMins(
                            this.props.session.publisher.Localization,
                          )}
                          handleFocusedInputChange={this.handleCalendarFocusedInputChange(true)}
                          timezone={timezone}
                          trackOnClose={() =>
                            this.trackEvent('SSAI Dashboard - Open Compare Calendar')
                          }
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Grid item>
              <Grid
                container
                direction='row'
                justify='flex-end'
                alignItems='flex-end'
                style={{ flexWrap: 'wrap' }}
                spacing={1}
              >
                {liveDensityRangesNetworkHome.map((densityRange: IDensityRange) => {
                  const { text, value } = densityRange;
                  const selected = this.state.densityRangeLive === value;
                  return (
                    <Grid item onClick={() => this.handleDensityRangeChangeLive(value)}>
                      <Button
                        size='small'
                        className={
                          selected ? classes.selectedTimeStamp : classes.unselectedTimeStamp
                        }
                      >
                        {text}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          )}
        </Grid>
      );
    }

    errorIfInvalidStateName = (stateVarName: string): void => {
      if (!Object.keys(this.state).includes(stateVarName)) {
        throw new Error(`No field named ${stateVarName} exists in state.`);
      }
    };

    getTypesForQuery = (typeName: string, allTypes: IStreamType[] | IDeviceType[]): string => {
      this.errorIfInvalidStateName(typeName);
      let types = '';
      if (this.state[typeName] && !this.state[typeName].selectedAll) {
        types = this.state[typeName].selected.map((type: string) => "'" + type + "'").join(',');
      }
      if ((this.state[typeName] || []).length === allTypes.length) {
        // Empty defaults to select all
        return '';
      }
      return types;
    };

    // helper function to remove CONNECTED_DEVICE as this isn't a supported
    // device type in snowflake yet for Stream events
    getStreamDeviceList = (deviceList: IMenuList): IMenuList => {
      return Object.fromEntries(
        Object.entries(deviceList).filter(([key]) => key !== 'CONNECTED_DEVICE'),
      );
    };

    /**
     * Returns 1 for if a filter of that type is applied, 0 o.w.
     */
    checkIfFilterActive = (typeName: string): number => {
      this.errorIfInvalidStateName(typeName);
      if (this.state[typeName].selected.length && !this.state[typeName].selectedAll) {
        return 1;
      }
      return 0;
    };

    countActiveFilters = (): number => {
      return (
        this.checkIfFilterActive('streamTypeState') +
        this.checkIfFilterActive('networkTypeState') +
        this.checkIfFilterActive('deviceTypeState')
      );
    };

    // filter menu render functions
    renderMenuTitle = (insideDropdown: boolean) => {
      const activeFilterCount = this.countActiveFilters();
      return (
        <Grid container direction='row' alignItems='center'>
          <Grid item>
            <Typography
              style={{
                marginLeft: -5,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'black',
              }}
            >
              FILTERS
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: 10 }}>
            <img src='/assets/shared/svg/FilterDropdownRoot.svg' />
          </Grid>
          {activeFilterCount !== 0 && (
            <Grid item style={{ marginLeft: 25 }}>
              <FilterActiveChip
                label={
                  activeFilterCount === 1
                    ? '1 Active Filter'
                    : `${activeFilterCount} Active Filters`
                }
              />
            </Grid>
          )}
          {this.state.toggleLiveData === ViewMode.LiveData && (
            <Grid item style={{ marginLeft: 25 }}>
              <LiveDataChip label='Live Data' />
            </Grid>
          )}
          {insideDropdown && activeFilterCount !== 0 && (
            <Grid item style={{ marginLeft: 10 }} onClick={() => this.handleClearFilters()}>
              <Typography
                style={{
                  marginLeft: 30,
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: '#4952FF',
                  textTransform: 'none',
                }}
              >
                Clear
              </Typography>
            </Grid>
          )}
        </Grid>
      );
    };

    renderRow = (
      typeName: string,
      dropDownName: string,
      menuTitleOverride: string,
      selectAllDefault: boolean,
    ): JSX.Element => {
      this.errorIfInvalidStateName(typeName);
      const typeState = this.state[typeName];
      const selectAll = typeState.selectedAll;
      const selectByName = !selectAll;

      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={0}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>{dropDownName}</Typography>
            </Grid>
            <Grid item xs={1}>
              <Grid container direction='row' alignItems='center'>
                <Grid item>
                  <Radio
                    size='small'
                    checked={selectAll}
                    onChange={() => this.handleViewAll(typeName, true)}
                  />
                </Grid>
                <Grid item>
                  <Typography style={{ fontSize: 12, color: selectAll ? 'black' : '#cecdcf' }}>
                    All
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={4} direction='row' alignItems='center'>
              <Grid container direction='row' alignItems='center'>
                <Grid item>
                  <Radio
                    size='small'
                    checked={selectByName}
                    onChange={() => this.handleViewAll(typeName, false)}
                  />
                </Grid>
                <Grid item>
                  <MaterialSearchDropdownMulti
                    fontSize='12px'
                    menuTitle={dropDownName}
                    width='150px'
                    selectAll={selectAllDefault}
                    list={this.state[typeName].typeList}
                    onChange={(list: IMenuList) => {
                      // set state
                      this.setState({
                        [typeName]: { ...this.state[typeName], typeList: list },
                      });
                    }}
                    disabled={selectAll}
                    handleMenuAcceptClick={(src: IMenuList) => {
                      this.handleTypeAcceptClick(typeName, src);
                    }}
                    menuTitleOverride={menuTitleOverride}
                    forceOpen={this.state[typeName].forceOpenList}
                    onlyShowNumberWhenClosed={true}
                    leftRightDropdownEnabled={true}
                    maxHeight={350}
                    enableFixed={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderTimezoneRow = () => {
      const { timezonesList } = this.state;
      return (
        <Grid item style={{ width: '100%' }}>
          <Grid container direction='row' alignItems='center' spacing={2}>
            <Grid item xs={3}>
              <Typography style={{ fontWeight: 500, fontSize: 12 }}>Timezone</Typography>
            </Grid>
            <Grid item xs={8} direction='row' alignItems='center'>
              <Grid item style={{ width: 240, marginLeft: 5 }}>
                <MaterialSearchDropdownMulti
                  fontSize='12px'
                  width='200px'
                  menuTitle='Timezone'
                  list={timezonesList}
                  enableMultiSelect={false}
                  onChange={(list: IMenuList) => {
                    this.setState({
                      timezonesList: list,
                    });
                  }}
                  handleMenuAcceptClick={(src: IMenuList) => {
                    this.handleTimezoneAcceptClick(src);
                  }}
                  leftRightDropdownEnabled={true}
                  maxHeight={350}
                  enableFixed={true}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );
    };

    renderFilterButton = () => {
      const menuTitleRow = {
        closeOnClick: false,
        render: () => this.renderMenuTitle(true),
      };
      const networkRow = {
        closeOnClick: false,
        render: () => this.renderRow('networkTypeState', 'Networks', 'Select Networks', true),
      };
      const streamTypeRow = {
        closeOnClick: false,
        render: () => this.renderRow('streamTypeState', 'Stream Type', 'By Name', false),
      };
      const deviceTypeRow = {
        closeOnClick: false,
        render: () => this.renderRow('deviceTypeState', 'Device Type', 'By Name', false),
      };
      const timezoneRow = {
        closeOnClick: false,
        render: this.renderTimezoneRow,
      };
      const menuButtonItems = [menuTitleRow, networkRow, streamTypeRow, deviceTypeRow, timezoneRow];
      return (
        <MenuButton
          titleRender={() => this.renderMenuTitle(false)}
          items={menuButtonItems}
          minWidth={700}
          trackOnOpen={() => this.trackEvent('SSAI Dashboard - Open Filter Box')}
          onClose={() => {
            setTimeout(() => {
              // give time for menus to close and decide if the menu has changed
              if (this.state.madeFilterChange) {
                this.state.toggleLiveData === ViewMode.Overview
                  ? this.loadDensityData()
                  : this.loadRealtimeAnalytics();
              }
            }, 500);
          }}
        />
      );
    };

    renderLiveDataGraph = (
      graphTitle: string,
      graphStateName: string,
      colorMap: Record<string, string>,
      graphData: IGraphDataTypes,
    ) => {
      const { classes, user } = this.props;
      const { densityRangeLive, toggleLiveData } = this.state;
      this.errorIfInvalidStateName(graphStateName);
      return (
        <div>
          <div style={{ marginTop: 5 }}>
            <DashboardGraphV2
              graphTitle={graphTitle}
              toggleLiveData={toggleLiveData === ViewMode.LiveData}
              mergeLiveData={false}
              classes={classes}
              densityRangeLive={densityRangeLive}
              handleDensityRangeChange={this.handleDensityRangeChangeLive}
              data1={[]}
              data2={[]}
              dataLive={this.state[graphStateName].data}
              compareEnabled={false}
              loaded={this.state[graphStateName].loading === LoadingStateHasData}
              loadingState1={this.state[graphStateName].loading}
              loadingState2={LoadingStateHasData}
              metricsInfo={this.state[graphStateName].cardDataDisplay}
              selectedMetricsColorMap={colorMap}
              selectedMetricsDataToggle={this.constructToggleData(
                graphData,
                this.state[graphStateName].selectedMetrics,
              )}
              selectedMetricsGraphData={graphData}
              currencySymbol='$'
              selectedMetrics={this.state[graphStateName].selectedMetrics}
              handleMetricsChange={(key: StreamDashboardCardDataKeys, color: string) => {
                this.updateToggleData(key, color, graphStateName as StreamDashboardCardDataKeys);
              }}
              user={user.user}
            />
          </div>
        </div>
      );
    };

    renderLiveData() {
      return (
        <div>
          {this.renderLiveDataGraph('Traffic', 'streamKPIState', liveKPIColorMap, liveKPIGraphData)}
          {this.renderLiveDataGraph(
            'Latency',
            'streamLatencyState',
            liveLatencyColorMap,
            liveLatencyGraphData,
          )}
          {this.renderLiveDataGraph(
            'Stream Errors',
            'streamErrorState',
            liveErrorColorMap,
            liveErrorGraphData,
          )}
        </div>
      );
    }

    renderCardsAndGraphAgg() {
      const {
        calEndDate,
        calStartDate,
        compStartDate,
        compEndDate,
        compareEnabled,
        data1,
        data2,
        loading1,
        loading2,
        densityRangeAgg,
        dashboardCardDataDisplay,
        dashboardCardDataCompareDisplay,
        selectedMetricsOverview,
        toggleLiveData,
      } = this.state;
      const { classes, user } = this.props;
      const isLoading = compareEnabled
        ? loading1 === LoadingStateLoading && loading2 === LoadingStateLoading
        : loading1 === LoadingStateLoading;
      return (
        <div style={{ marginTop: 5 }}>
          {isLoading && <LinearProgress style={{ width: '100%', height: 5, marginBottom: 7 }} />}
          <DashboardCards
            loaded={
              compareEnabled
                ? loading1 === LoadingStateHasData && loading2 === LoadingStateHasData
                : loading1 === LoadingStateHasData
            }
            loading={
              compareEnabled
                ? loading1 === LoadingStateLoading && loading2 === LoadingStateLoading
                : loading1 === LoadingStateLoading
            }
            entries={dashboardCardDataDisplay}
            entriesCompare={dashboardCardDataCompareDisplay}
            compareEnabled={compareEnabled}
            menuItemList={streamDashboardCardDropdownList}
            handleSwitchActiveMetric={this.handleSwitchActiveMetric}
            handleSwitchCardSelected={this.handleSwitchCardSelected}
            selectedMetricsColorMap={selectedMetricsColorMap}
            selectedMetrics={selectedMetricsOverview}
          />
          <DashboardGraphV2
            graphTitle=''
            toggleLiveData={toggleLiveData === ViewMode.LiveData}
            mergeLiveData={false}
            classes={classes}
            densityRange={densityRangeAgg}
            handleDensityRangeChange={this.handleDensityRangeChangeAgg}
            calStartDate={calStartDate}
            calEndDate={calEndDate}
            compStartDate={compStartDate}
            compEndDate={compEndDate}
            data1={data1}
            data2={data2}
            dataLive={[]}
            compareEnabled={compareEnabled}
            loaded={
              compareEnabled
                ? loading1 === LoadingStateHasData && loading2 === LoadingStateHasData
                : loading1 === LoadingStateHasData
            }
            loadingState1={loading1}
            loadingState2={loading2}
            metricsInfo={dashboardCardDataDisplay}
            selectedMetricsColorMap={selectedMetricsColorMap}
            selectedMetricsDataToggle={{} as ISelectedMetrics}
            selectedMetricsGraphData={{} as IGraphDataTypes}
            user={user.user}
            currencySymbol='$'
            selectedMetrics={selectedMetricsOverview}
            handleMetricsChange={() => null}
          />
        </div>
      );
    }

    render() {
      const { snackbarMessage, snackbarError } = this.state;
      return (
        <Panels maxWidth vertical relative minWidthMain>
          {this.renderTop()}
          <Panel scroll largePadding>
            {this.state.toggleLiveData ? this.renderLiveData() : this.renderCardsAndGraphAgg()}
            <SnackbarView
              autoHideDuration={12000}
              open={!!snackbarMessage}
              error={!!snackbarError}
              onClose={this.snackbarClose}
              messageColor=''
              backgroundColor=''
              closeColor=''
            >
              {snackbarMessage}
            </SnackbarView>
          </Panel>
        </Panels>
      );
    }
  },
);

// Percentage change = (new_value - old_value) / old_value
const calculatePercentageChange = (val1: number, val2: number): string => {
  if (val1 === 0) {
    return '0.00%';
  }
  if (val2 === 0 && val1 === 0) {
    return '0.00%';
  }
  if (val2 === 0) {
    return '100%';
  }
  return (((val1 - val2) * 100) / val2).toFixed(2) + '%';
};
